import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Save,
  ExternalLink,
  GripVertical,
} from "lucide-react";

const emptyProduct = {
  section_key: "",
  name: "",
  description: "",
  price: "",
  image_url: "",
  affiliate_url: "",
  button_label: "SHOP NOW",
  order: 0,
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">
      {label} {hint && <span className="text-white/20 normal-case tracking-normal">{hint}</span>}
    </label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 ${props.className || ""}`}
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className={`w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 resize-none ${props.className || ""}`}
  />
);

export default function AffiliateManager() {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [productOpen, setProductOpen] = useState(false);
  const [productForm, setProductForm] = useState({ ...emptyProduct });
  const [editProductId, setEditProductId] = useState(null);

  const [sectionOpen, setSectionOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ key: "", title: "", description: "", order: 0 });
  const [editSectionKey, setEditSectionKey] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get(`/affiliate/config?_t=${Date.now()}`);
      const { products: prods = [], ...cfg } = data;
      // Defensive defaults
      cfg.hero = cfg.hero || { title: "", subtitle: "", supporting: "", disclaimer: "" };
      cfg.featured = cfg.featured || {
        enabled: false, name: "", description: "", price: "", image_url: "", affiliate_url: "", button_label: "SHOP NOW",
      };
      cfg.cta = cfg.cta || { title: "", subtext: "", button_label: "EXPLORE MORE", button_url: "" };
      cfg.sections = cfg.sections || [];
      cfg.footer_disclaimer = cfg.footer_disclaimer || "";
      setConfig(cfg);
      setProducts(prods);
      setLoading(false);
    } catch {
      toast.error("Failed to load affiliate config");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const setHero = (k, v) => setConfig((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));
  const setFeatured = (k, v) => setConfig((c) => ({ ...c, featured: { ...c.featured, [k]: v } }));
  const setCta = (k, v) => setConfig((c) => ({ ...c, cta: { ...c.cta, [k]: v } }));

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const { products: _omit, ...payload } = config;
      await api.put("/affiliate/config", payload);
      toast.success("Layout saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Section CRUD (in-config) ──
  const openAddSection = () => {
    setEditSectionKey(null);
    setSectionForm({ key: "", title: "", description: "", order: (config?.sections?.length || 0) + 1 });
    setSectionOpen(true);
  };
  const openEditSection = (s) => {
    setEditSectionKey(s.key);
    setSectionForm({ ...s });
    setSectionOpen(true);
  };
  const saveSection = async () => {
    const key = (sectionForm.key || "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "-");
    const title = (sectionForm.title || "").trim();
    if (!key || !title) {
      toast.error("Section key and title are required");
      return;
    }
    let nextSections;
    if (editSectionKey) {
      nextSections = config.sections.map((s) =>
        s.key === editSectionKey ? { ...s, ...sectionForm, key } : s
      );
    } else {
      if (config.sections.some((s) => s.key === key)) {
        toast.error("A section with that key already exists");
        return;
      }
      nextSections = [...config.sections, { ...sectionForm, key }];
    }
    const newConfig = { ...config, sections: nextSections };
    const { products: _o, ...payload } = newConfig;
    try {
      await api.put("/affiliate/config", payload);
      // If renaming the key, propagate to products with the old key
      if (editSectionKey && editSectionKey !== key) {
        const affected = products.filter((p) => p.section_key === editSectionKey);
        await Promise.all(
          affected.map((p) =>
            api.put(`/affiliate/products/${p.id}`, { ...p, section_key: key })
          )
        );
      }
      setConfig(newConfig);
      setSectionOpen(false);
      toast.success(editSectionKey ? "Section updated" : "Section added");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteSection = async (s) => {
    const inSection = products.filter((p) => p.section_key === s.key);
    if (inSection.length > 0) {
      if (!window.confirm(`Delete section "${s.title}" and ${inSection.length} product(s) inside it?`)) return;
      await Promise.all(inSection.map((p) => api.delete(`/affiliate/products/${p.id}`)));
    } else {
      if (!window.confirm(`Delete section "${s.title}"?`)) return;
    }
    const nextSections = config.sections.filter((x) => x.key !== s.key);
    const newConfig = { ...config, sections: nextSections };
    const { products: _o, ...payload } = newConfig;
    try {
      await api.put("/affiliate/config", payload);
      toast.success("Section deleted");
      setConfig(newConfig);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ── Product CRUD ──
  const openAddProduct = (sectionKey = "") => {
    setEditProductId(null);
    setProductForm({ ...emptyProduct, section_key: sectionKey || config?.sections?.[0]?.key || "" });
    setProductOpen(true);
  };
  const openEditProduct = (p) => {
    setEditProductId(p.id);
    setProductForm({ ...emptyProduct, ...p });
    setProductOpen(true);
  };
  const saveProduct = async () => {
    if (!productForm.name?.trim()) return toast.error("Product name is required");
    if (!productForm.affiliate_url?.trim()) return toast.error("Affiliate URL is required");
    if (!productForm.section_key) return toast.error("Pick a section");
    try {
      if (editProductId) {
        await api.put(`/affiliate/products/${editProductId}`, productForm);
      } else {
        await api.post("/affiliate/products", productForm);
      }
      toast.success(editProductId ? "Product updated" : "Product added");
      setProductOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/affiliate/products/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading || !config) return <p className="text-white/30">Loading...</p>;

  const sectionOptions = config.sections.map((s) => ({ value: s.key, label: s.title }));

  return (
    <div data-testid="admin-affiliate-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl">AFFILIATE SHOP</h1>
          <p className="text-white/40 text-sm">Curated outbound product showcase</p>
        </div>
        <Button
          data-testid="affiliate-save-layout-btn"
          onClick={saveConfig}
          disabled={saving}
          className="bg-[#D4AF37] text-black hover:bg-white text-sm"
        >
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Layout"}
        </Button>
      </div>

      {/* HERO */}
      <Card title="Hero Section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title">
            <Input
              data-testid="affiliate-hero-title-input"
              value={config.hero.title}
              onChange={(e) => setHero("title", e.target.value)}
              placeholder="LUXIFER 6 APPROVED GEAR"
            />
          </Field>
          <Field label="Subtitle">
            <Input
              value={config.hero.subtitle}
              onChange={(e) => setHero("subtitle", e.target.value)}
              placeholder="Style. Power. Presence."
            />
          </Field>
          <Field label="Supporting Line">
            <Input
              value={config.hero.supporting}
              onChange={(e) => setHero("supporting", e.target.value)}
              placeholder="What I wear. What I stand on."
            />
          </Field>
          <Field label="Disclaimer">
            <Input
              value={config.hero.disclaimer}
              onChange={(e) => setHero("disclaimer", e.target.value)}
              placeholder="As an Amazon Associate, I earn from qualifying purchases."
            />
          </Field>
        </div>
      </Card>

      {/* FEATURED */}
      <Card
        title="Featured Product"
        right={
          <div className="flex items-center gap-2">
            <Switch
              data-testid="affiliate-featured-toggle"
              checked={!!config.featured.enabled}
              onCheckedChange={(v) => setFeatured("enabled", v)}
            />
            <span className="text-xs text-white/50">{config.featured.enabled ? "On" : "Off"}</span>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name">
            <Input
              value={config.featured.name}
              onChange={(e) => setFeatured("name", e.target.value)}
              placeholder="Product name"
            />
          </Field>
          <Field label="Price" hint="(optional, free text)">
            <Input
              value={config.featured.price}
              onChange={(e) => setFeatured("price", e.target.value)}
              placeholder="$249"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Description">
              <TextArea
                rows={2}
                value={config.featured.description}
                onChange={(e) => setFeatured("description", e.target.value)}
                placeholder="Short bold description"
              />
            </Field>
          </div>
          <Field label="Affiliate URL">
            <Input
              value={config.featured.affiliate_url}
              onChange={(e) => setFeatured("affiliate_url", e.target.value)}
              placeholder="https://amzn.to/..."
            />
          </Field>
          <Field label="Button Label">
            <Input
              value={config.featured.button_label}
              onChange={(e) => setFeatured("button_label", e.target.value)}
              placeholder="SHOP NOW"
            />
          </Field>
          <div className="md:col-span-2">
            <FileUpload
              label="Featured Image"
              accept="image/*"
              value={config.featured.image_url}
              onChange={(v) => setFeatured("image_url", v)}
            />
          </div>
        </div>
      </Card>

      {/* SECTIONS */}
      <Card
        title="Sections"
        right={
          <Button
            data-testid="affiliate-add-section-btn"
            onClick={openAddSection}
            variant="outline"
            className="border-white/10 text-white/70 hover:text-white hover:border-[#D4AF37]/40"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Section
          </Button>
        }
      >
        {config.sections.length === 0 ? (
          <p className="text-white/30 text-sm py-6 text-center">No sections yet. Click "Add Section" to start.</p>
        ) : (
          <div className="space-y-2">
            {[...config.sections]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((s) => (
                <div
                  key={s.key}
                  data-testid={`affiliate-section-row-${s.key}`}
                  className="flex items-center gap-3 bg-[#0A0A0A] border border-white/5 p-3 rounded hover:border-white/10 transition"
                >
                  <GripVertical className="w-4 h-4 text-white/15" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{s.title}</p>
                    <p className="text-[11px] text-white/35 truncate">
                      key: {s.key} · order: {s.order || 0} · {products.filter((p) => p.section_key === s.key).length} products
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => openAddProduct(s.key)} className="text-white/50 hover:text-[#D4AF37]">
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEditSection(s)} className="text-white/40 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteSection(s)} className="text-white/40 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
          </div>
        )}
      </Card>

      {/* PRODUCTS */}
      <Card
        title="Products"
        right={
          <Button
            data-testid="affiliate-add-product-btn"
            onClick={() => openAddProduct()}
            className="bg-[#D4AF37] text-black hover:bg-white text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        }
      >
        {products.length === 0 ? (
          <p className="text-white/30 text-sm py-6 text-center">No products yet.</p>
        ) : (
          <div className="space-y-2">
            {products.map((p) => {
              const section = config.sections.find((s) => s.key === p.section_key);
              return (
                <div
                  key={p.id}
                  data-testid={`affiliate-product-row-${p.id}`}
                  className="flex items-center gap-4 bg-[#0A0A0A] border border-white/5 p-3 rounded hover:border-white/10 transition"
                >
                  {p.image_url ? (
                    <img src={getFileUrl(p.image_url)} alt="" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-[#111] rounded flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <p className="text-[11px] text-white/35 truncate">
                      {section ? section.title : <span className="text-red-400/70">orphan: {p.section_key}</span>}
                      {p.price ? ` · ${String(p.price).startsWith("$") ? p.price : `$${p.price}`}` : ""}
                    </p>
                  </div>
                  {p.affiliate_url && (
                    <a
                      href={p.affiliate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/30 hover:text-[#D4AF37] transition"
                      title="Open affiliate link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => openEditProduct(p)} className="text-white/40 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteProduct(p.id)} className="text-white/40 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* CTA */}
      <Card title="Bottom CTA">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title">
            <Input
              value={config.cta.title}
              onChange={(e) => setCta("title", e.target.value)}
              placeholder="LEVEL UP YOUR LOOK"
            />
          </Field>
          <Field label="Button Label">
            <Input
              value={config.cta.button_label}
              onChange={(e) => setCta("button_label", e.target.value)}
              placeholder="EXPLORE MORE"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Subtext">
              <Input
                value={config.cta.subtext}
                onChange={(e) => setCta("subtext", e.target.value)}
                placeholder="Luxury isn't luck. It's a decision."
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Button URL">
              <Input
                value={config.cta.button_url}
                onChange={(e) => setCta("button_url", e.target.value)}
                placeholder="https://amzn.to/..."
              />
            </Field>
          </div>
        </div>
      </Card>

      {/* FOOTER DISCLAIMER */}
      <Card title="Footer Disclaimer">
        <Input
          value={config.footer_disclaimer}
          onChange={(e) => setConfig({ ...config, footer_disclaimer: e.target.value })}
          placeholder="As an Amazon Associate, I earn from qualifying purchases."
        />
      </Card>

      {/* Sticky save reminder */}
      <div className="text-center mt-8 pb-4">
        <Button
          onClick={saveConfig}
          disabled={saving}
          className="bg-[#D4AF37] text-black hover:bg-white px-8"
        >
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Layout"}
        </Button>
      </div>

      {/* PRODUCT DIALOG */}
      <Dialog open={productOpen} onOpenChange={setProductOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editProductId ? "EDIT PRODUCT" : "ADD PRODUCT"}
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Outbound affiliate link. Opens in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Section">
                <select
                  data-testid="affiliate-product-section-select"
                  value={productForm.section_key}
                  onChange={(e) => setProductForm((f) => ({ ...f, section_key: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]"
                >
                  <option value="">— pick a section —</option>
                  {sectionOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Name *">
                <Input
                  data-testid="affiliate-product-name-input"
                  value={productForm.name}
                  onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
                />
              </Field>
            </div>
            <Field label="Description">
              <TextArea
                rows={2}
                value={productForm.description}
                onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Price" hint="(optional, free text)">
                <Input
                  value={productForm.price}
                  onChange={(e) => setProductForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="$129"
                />
              </Field>
              <Field label="Order" hint="(lower shows first)">
                <Input
                  type="number"
                  value={productForm.order}
                  onChange={(e) => setProductForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                />
              </Field>
            </div>
            <Field label="Affiliate URL *">
              <Input
                data-testid="affiliate-product-url-input"
                value={productForm.affiliate_url}
                onChange={(e) => setProductForm((f) => ({ ...f, affiliate_url: e.target.value }))}
                placeholder="https://amzn.to/..."
              />
            </Field>
            <Field label="Button Label">
              <Input
                value={productForm.button_label}
                onChange={(e) => setProductForm((f) => ({ ...f, button_label: e.target.value }))}
                placeholder="SHOP NOW"
              />
            </Field>
            <FileUpload
              label="Product Image"
              accept="image/*"
              value={productForm.image_url}
              onChange={(v) => setProductForm((f) => ({ ...f, image_url: v }))}
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setProductOpen(false)} className="border-white/10 text-white/60">
                Cancel
              </Button>
              <Button
                data-testid="affiliate-product-save-btn"
                onClick={saveProduct}
                className="bg-[#D4AF37] text-black hover:bg-white"
              >
                {editProductId ? "Update" : "Add"} Product
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SECTION DIALOG */}
      <Dialog open={sectionOpen} onOpenChange={setSectionOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editSectionKey ? "EDIT SECTION" : "ADD SECTION"}
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Sections group products on the public page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Key" hint="(slug, e.g. luxury, bags)">
              <Input
                data-testid="affiliate-section-key-input"
                value={sectionForm.key}
                onChange={(e) => setSectionForm((f) => ({ ...f, key: e.target.value }))}
                placeholder="luxury"
              />
            </Field>
            <Field label="Title *">
              <Input
                data-testid="affiliate-section-title-input"
                value={sectionForm.title}
                onChange={(e) => setSectionForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="LUXURY PICKS"
              />
            </Field>
            <Field label="Description">
              <TextArea
                rows={2}
                value={sectionForm.description}
                onChange={(e) => setSectionForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
            <Field label="Order" hint="(lower shows first)">
              <Input
                type="number"
                value={sectionForm.order}
                onChange={(e) => setSectionForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))}
              />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setSectionOpen(false)} className="border-white/10 text-white/60">
                Cancel
              </Button>
              <Button
                data-testid="affiliate-section-save-btn"
                onClick={saveSection}
                className="bg-[#D4AF37] text-black hover:bg-white"
              >
                {editSectionKey ? "Update" : "Add"} Section
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ title, right, children }) {
  return (
    <section className="bg-[#0A0A0A] border border-white/5 rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-heading text-lg tracking-wide">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}
